import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Database } from '@/integrations/supabase/types';

type TaskRow = Database['public']['Tables']['tasks']['Row'];
type TaskItemRow = Database['public']['Tables']['task_items']['Row'];
type TaskCommentRow = Database['public']['Tables']['task_comments']['Row'];

export interface TaskWithMeta extends TaskRow {
  items: TaskItemRow[];
  comment_count: number;
  items_done: number;
  items_total: number;
  website_name: string | null;
}

export interface TaskComment extends TaskCommentRow {
  author_email: string | null;
}

// ============ LISTA TASKOW ============

export const useTaskBoard = () => {
  const [tasks, setTasks] = useState<TaskWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchTasks = useCallback(async () => {
    if (!user) return;

    const { data: tasksData, error } = await supabase
      .from('tasks')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks:', error);
      setLoading(false);
      return;
    }

    // Load website names for domain badges
    const websiteIds = [...new Set((tasksData || []).map((t) => t.website_id).filter(Boolean))];
    let websiteMap: Record<string, string> = {};
    if (websiteIds.length > 0) {
      const { data: websites } = await supabase
        .from('websites')
        .select('id, name')
        .in('id', websiteIds);
      if (websites) {
        websiteMap = Object.fromEntries(websites.map((w) => [w.id, w.name]));
      }
    }

    const tasksWithMeta: TaskWithMeta[] = await Promise.all(
      (tasksData || []).map(async (task) => {
        const [itemsRes, commentsRes] = await Promise.all([
          supabase
            .from('task_items')
            .select('*')
            .eq('task_id', task.id)
            .order('sort_order', { ascending: true }),
          supabase
            .from('task_comments')
            .select('*', { count: 'exact', head: true })
            .eq('task_id', task.id),
        ]);

        const items = itemsRes.data || [];
        return {
          ...task,
          items,
          comment_count: commentsRes.count || 0,
          items_done: items.filter((i) => i.is_checked).length,
          items_total: items.length,
          website_name: task.website_id ? (websiteMap[task.website_id] || null) : null,
        };
      })
    );

    setTasks(tasksWithMeta);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Realtime
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('tasks-board')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => fetchTasks())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_items' }, () => fetchTasks())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments' }, () => fetchTasks())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchTasks]);

  const createTask = async (
    title: string,
    description: string,
    priority: 'low' | 'medium' | 'high',
    checklistItems: string[],
    websiteId?: string | null
  ) => {
    if (!user) return { error: 'Nie zalogowany' };

    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .insert({
        user_id: user.id,
        title,
        description,
        priority,
        ...(websiteId ? { website_id: websiteId } : {}),
      })
      .select()
      .single();

    if (taskError) return { error: taskError.message };

    if (checklistItems.length > 0 && task) {
      const items = checklistItems
        .filter((c) => c.trim())
        .map((content, i) => ({
          task_id: task.id,
          content: content.trim(),
          sort_order: i,
        }));

      if (items.length > 0) {
        const { error: itemsError } = await supabase.from('task_items').insert(items);
        if (itemsError) return { error: itemsError.message };
      }
    }

    return { error: null, taskId: task?.id };
  };

  const updateTaskStatus = async (taskId: string, status: 'open' | 'in_progress' | 'done') => {
    const { error } = await supabase
      .from('tasks')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', taskId);
    return { error: error?.message || null };
  };

  const updateTaskPriority = async (taskId: string, priority: 'low' | 'medium' | 'high') => {
    const { error } = await supabase
      .from('tasks')
      .update({ priority, updated_at: new Date().toISOString() })
      .eq('id', taskId);
    return { error: error?.message || null };
  };

  const deleteTask = async (taskId: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);
    return { error: error?.message || null };
  };

  return {
    tasks,
    loading,
    createTask,
    updateTaskStatus,
    updateTaskPriority,
    deleteTask,
    refresh: fetchTasks,
  };
};

// ============ SZCZEGOLY TASKU ============

export const useTaskDetail = (taskId: string | null) => {
  const [task, setTask] = useState<TaskRow | null>(null);
  const [items, setItems] = useState<TaskItemRow[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchDetail = useCallback(async () => {
    if (!taskId || !user) {
      setLoading(false);
      return;
    }

    const [taskRes, itemsRes, commentsRes] = await Promise.all([
      supabase.from('tasks').select('*').eq('id', taskId).single(),
      supabase.from('task_items').select('*').eq('task_id', taskId).order('sort_order', { ascending: true }),
      supabase.from('task_comments').select('*').eq('task_id', taskId).order('created_at', { ascending: true }),
    ]);

    if (taskRes.data) setTask(taskRes.data);
    if (itemsRes.data) setItems(itemsRes.data);
    if (commentsRes.data) {
      setComments(commentsRes.data.map((c) => ({ ...c, author_email: null })));
    }
    setLoading(false);
  }, [taskId, user]);

  useEffect(() => {
    setLoading(true);
    fetchDetail();
  }, [fetchDetail]);

  // Realtime
  useEffect(() => {
    if (!taskId || !user) return;

    const channel = supabase
      .channel(`task-detail-${taskId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `id=eq.${taskId}` }, () => fetchDetail())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_items', filter: `task_id=eq.${taskId}` }, () => fetchDetail())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments', filter: `task_id=eq.${taskId}` }, () => fetchDetail())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, user, fetchDetail]);

  // Checklist operations
  const toggleItem = async (itemId: string, isChecked: boolean) => {
    const { error } = await supabase
      .from('task_items')
      .update({ is_checked: !isChecked })
      .eq('id', itemId);
    if (!error) {
      await supabase.from('tasks').update({ updated_at: new Date().toISOString() }).eq('id', taskId!);
    }
    return { error: error?.message || null };
  };

  const addItem = async (content: string) => {
    if (!taskId) return { error: 'Brak tasku' };
    const maxOrder = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 0;
    const { error } = await supabase.from('task_items').insert({
      task_id: taskId,
      content,
      sort_order: maxOrder,
    });
    if (!error) {
      await supabase.from('tasks').update({ updated_at: new Date().toISOString() }).eq('id', taskId);
    }
    return { error: error?.message || null };
  };

  const removeItem = async (itemId: string) => {
    const { error } = await supabase.from('task_items').delete().eq('id', itemId);
    return { error: error?.message || null };
  };

  const updateItem = async (itemId: string, content: string) => {
    const { error } = await supabase.from('task_items').update({ content }).eq('id', itemId);
    return { error: error?.message || null };
  };

  // Comments
  const addComment = async (content: string) => {
    if (!user || !taskId) return { error: 'Brak tasku lub uzytkownika' };
    const { error } = await supabase.from('task_comments').insert({
      task_id: taskId,
      user_id: user.id,
      content,
    });
    if (!error) {
      await supabase.from('tasks').update({ updated_at: new Date().toISOString() }).eq('id', taskId);
    }
    return { error: error?.message || null };
  };

  const deleteComment = async (commentId: string) => {
    const { error } = await supabase.from('task_comments').delete().eq('id', commentId);
    return { error: error?.message || null };
  };

  return {
    task,
    items,
    comments,
    loading,
    toggleItem,
    addItem,
    removeItem,
    updateItem,
    addComment,
    deleteComment,
    refresh: fetchDetail,
  };
};
